/**
 * Client-side video compression before upload. Reduces file size for faster uploads.
 * When supported, re-encodes at lower resolution/bitrate; otherwise returns null (use original).
 * @param {File} file - Video file
 * @param {(percent: number) => void} [onProgress] - Called with 0–100 during compression
 * @returns {Promise<Blob|null>} Compressed blob or null to use original
 */
const MAX_SIZE_TO_COMPRESS = 4 * 1024 * 1024; // 4MB – only try to compress larger files

export function compressVideo(file, onProgress) {
  if (!file || file.size < MAX_SIZE_TO_COMPRESS) return Promise.resolve(null);
  return compressVideoWithCanvas(file, onProgress).catch(() => null);
}

function compressVideoWithCanvas(file, onProgress) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const url = URL.createObjectURL(file);
    let lastReportedPercent = -1;
    const reportProgress = (percent) => {
      if (typeof onProgress !== 'function') return;
      const p = Math.min(99, Math.round(percent));
      if (p <= lastReportedPercent) return;
      lastReportedPercent = p;
      onProgress(p);
    };
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.remove();
      if (recorder && recorder.state !== 'inactive') try { recorder.stop(); } catch {}
    };
    let recorder;
    video.onerror = () => { cleanup(); reject(new Error('Video load failed')); };
    video.onloadedmetadata = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) { cleanup(); reject(new Error('No dimensions')); return; }
      const scale = Math.min(1, 720 / w);
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) { cleanup(); reject(new Error('No canvas')); return; }
      const stream = canvas.captureStream(15);
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
      try {
        recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 800000 });
      } catch { cleanup(); reject(new Error('MediaRecorder unsupported')); return; }
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        if (typeof onProgress === 'function') onProgress(100);
        URL.revokeObjectURL(url);
        video.remove();
        if (chunks.length === 0) { resolve(null); return; }
        resolve(new Blob(chunks, { type: mime.split(';')[0] }));
      };
      recorder.onerror = () => { cleanup(); reject(new Error('Encode failed')); };
      recorder.start(200);
      const startTime = Date.now();
      const drawFrame = () => {
        if (video.ended || video.paused) { recorder.stop(); return; }
        const duration = video.duration;
        if (duration > 0 && isFinite(duration)) {
          reportProgress((video.currentTime / duration) * 100);
        } else {
          // Fallback when duration not yet available (e.g. some iOS/Safari): show progress by elapsed time (cap at 85%)
          const elapsedSec = (Date.now() - startTime) / 1000;
          reportProgress(Math.min(85, elapsedSec * 2));
        }
        ctx.drawImage(video, 0, 0, cw, ch);
        requestAnimationFrame(drawFrame);
      };
      const startDrawLoop = () => requestAnimationFrame(drawFrame);
      video.onplaying = startDrawLoop;
      video.onseeked = startDrawLoop;
      // 2x playback so compression finishes in ~half the time
      video.play().then(() => { video.playbackRate = 2; startDrawLoop(); }).catch(() => { try { recorder.stop(); } catch {} });
    };
    video.src = url;
    video.load();
  });
}
